/**
 * ODL Services route bundle — the field-services workspace (definitions,
 * projects, site jobs, work forms, reporting, crews) for services, tech
 * support, accounting, and crew-manager users. Gated behind the
 * per-tenant EXCLUDE_SERVICES build flag so the area and its lazy chunks
 * drop out of a tenant build that does not license services.
 *
 * Contains:
 * - servicesRoutes — array of <Route> elements, or null when excluded
 */
import React from 'react';
import { Navigate, Route } from 'react-router-dom';

import {
  determineIsAccountingUser,
  determineIsCrewManagerUser,
  determineIsServicesUser,
  determineIsTechSupportUser,
  determineServicesAccess,
} from '@sophocles/router/checkCredentials';
import {
  servicesBillingRedirectPath,
  servicesCrewsPath,
  servicesCustomerPricesPath,
  servicesDefinitionsPath,
  servicesHomePath,
  servicesProjectPath,
  servicesProjectSiteGroupPath,
  servicesProjectsRedirectPath,
  servicesReportingPath,
  servicesSiteJobsPath,
  servicesWorkFormJobPath,
  servicesWorkFormPath,
} from '@sophocles/router/paths';
import RequireAuth from '@sophocles/router/RequireAuth';

// --- Build-time DCE block ---
// Gated on the bare DefinePlugin global so webpack can prune the lazy()
// import() calls (and the resulting chunks) at build time when the flag
// is true.  Never extract the global into a variable — that would defeat
// tree-shaking.

export let servicesRoutes = null;
if (!__EXCLUDE_SERVICES__) {
  const ServicesDashboardView = React.lazy(() => import('@sophocles/views/services/ServicesDashboardView'));
  const ServiceDefinitionsView = React.lazy(() => import('@sophocles/views/services/ServiceDefinitionsView'));
  const CustomerServicePriceView = React.lazy(() => import('@sophocles/views/services/CustomerServicePriceView'));
  const PlantsGridView = React.lazy(() => import('@sophocles/views/services/PlantsGridView'));
  const SitesGridView = React.lazy(() => import('@sophocles/views/services/SitesGridView'));
  const SiteJobsView = React.lazy(() => import('@sophocles/views/services/SiteJobsView'));
  const WorkFormView = React.lazy(() => import('@sophocles/views/services/WorkFormView'));
  const ReportingView = React.lazy(() => import('@sophocles/views/services/ReportingView'));
  const CrewsView = React.lazy(() => import('@sophocles/views/services/CrewsView'));

  servicesRoutes = [
    <Route
      key={servicesHomePath.pattern}
      path={servicesHomePath.pattern}
      element={<RequireAuth component={ServicesDashboardView} onEnter={determineIsServicesUser} />}
    />,
    <Route
      key={servicesDefinitionsPath.pattern}
      path={servicesDefinitionsPath.pattern}
      element={<RequireAuth component={ServiceDefinitionsView} onEnter={determineIsTechSupportUser} />}
    />,
    <Route
      key={servicesProjectsRedirectPath.pattern}
      path={servicesProjectsRedirectPath.pattern}
      element={<Navigate to={servicesHomePath.pattern} replace />}
    />,
    <Route
      key={servicesCustomerPricesPath.pattern}
      path={servicesCustomerPricesPath.pattern}
      element={<RequireAuth component={CustomerServicePriceView} onEnter={determineIsAccountingUser} />}
    />,
    <Route
      key={servicesProjectPath.pattern}
      path={servicesProjectPath.pattern}
      element={<RequireAuth component={PlantsGridView} onEnter={determineServicesAccess} />}
    />,
    <Route
      key={servicesProjectSiteGroupPath.pattern}
      path={servicesProjectSiteGroupPath.pattern}
      element={<RequireAuth component={SitesGridView} onEnter={determineServicesAccess} />}
    />,
    <Route
      key={servicesSiteJobsPath.pattern}
      path={servicesSiteJobsPath.pattern}
      element={<RequireAuth component={SiteJobsView} onEnter={determineServicesAccess} />}
    />,
    <Route
      key={servicesWorkFormPath.pattern}
      path={servicesWorkFormPath.pattern}
      element={<RequireAuth component={WorkFormView} onEnter={determineServicesAccess} />}
    />,
    <Route
      key={servicesWorkFormJobPath.pattern}
      path={servicesWorkFormJobPath.pattern}
      element={<RequireAuth component={WorkFormView} onEnter={determineServicesAccess} />}
    />,
    <Route
      key={servicesReportingPath.pattern}
      path={servicesReportingPath.pattern}
      element={<RequireAuth component={ReportingView} onEnter={determineIsAccountingUser} />}
    />,
    <Route
      key={servicesBillingRedirectPath.pattern}
      path={servicesBillingRedirectPath.pattern}
      element={<Navigate to={servicesReportingPath.pattern} replace />}
    />,
    <Route
      key={servicesCrewsPath.pattern}
      path={servicesCrewsPath.pattern}
      element={<RequireAuth component={CrewsView} onEnter={determineIsCrewManagerUser} />}
    />,
  ];
}
